import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { setInterrupted, setSignalLost } from '../model/transcriptorSlice';
import {
  selectConsultationId,
  selectFragmentId,
  selectIsPaused,
  selectIsOffline,
  selectIsProcessing,
  selectIsRecording,
} from '../model/transcriptorSlice.selectors';

const INTERRUPTION_CHECK_INTERVAL = 10_000; // Check every 10 seconds
const INTERRUPTION_THRESHOLD = 60_000; // 1 minute

type UseRecordingInterruptionDetectionParams = {
  getLastActivityTimestamp: () => number;
  /** True once the audio track has ended: the device is gone for good. */
  hasCaptureEnded: () => boolean;
  /** Runs before the store is told, so capture is torn down in the same tick. */
  onInterrupted?: () => void;
  onSignalLostChange?: (isSignalLost: boolean) => void;
};

/**
 * Two different outcomes on purpose. An ended track cannot produce audio
 * again, so the recording is interrupted and capture released. Prolonged
 * silence is only a warning: the mic may be muted, unplugged for a moment,
 * or the room may simply be quiet, so capture keeps running and the warning
 * clears itself when signal returns.
 */
export const useRecordingInterruptionDetection = ({
  getLastActivityTimestamp,
  hasCaptureEnded,
  onInterrupted,
  onSignalLostChange,
}: UseRecordingInterruptionDetectionParams) => {
  const dispatch = useAppDispatch();
  const isRecording = useAppSelector(selectIsRecording);
  const isProcessing = useAppSelector(selectIsProcessing);
  const isPaused = useAppSelector(selectIsPaused);
  const isOffline = useAppSelector(selectIsOffline);
  const consultationId = useAppSelector(selectConsultationId);
  const fragmentId = useAppSelector(selectFragmentId);
  const isPausedRef = useRef(isPaused);
  const isOfflineRef = useRef(isOffline);

  // Keep refs in sync to avoid stale closures
  isPausedRef.current = isPaused;
  isOfflineRef.current = isOffline;

  useEffect(() => {
    if (!isRecording || isProcessing) {
      return;
    }

    let reportedSignalLost = false;

    const checkInterval = window.setInterval(() => {
      if (hasCaptureEnded()) {
        onInterrupted?.();
        dispatch(setInterrupted());
        window.clearInterval(checkInterval);
        return;
      }

      // Don't trigger interruption if paused or offline
      if (isPausedRef.current || isOfflineRef.current) {
        return;
      }

      const lastActivity = getLastActivityTimestamp();
      const signalLost =
        lastActivity > 0 && Date.now() - lastActivity >= INTERRUPTION_THRESHOLD;

      if (signalLost !== reportedSignalLost) {
        reportedSignalLost = signalLost;
        onSignalLostChange?.(signalLost);
        dispatch(setSignalLost(signalLost));
      }
    }, INTERRUPTION_CHECK_INTERVAL);

    return () => {
      window.clearInterval(checkInterval);
    };
  }, [
    isRecording,
    isProcessing,
    consultationId,
    fragmentId,
    getLastActivityTimestamp,
    hasCaptureEnded,
    onInterrupted,
    onSignalLostChange,
  ]);
};
